import { RimbuError } from '@rimbu/base';
import { OptLazy, TraverseState, Update, } from '@rimbu/common';
import { Stream } from '@rimbu/stream';
export class OrderedMapBuilder {
    constructor(context, source) {
        this.context = context;
        this.source = source;
        this._lock = false;
        // prettier-ignore
        this.hasKey = (key) => {
            return this.source?.hasKey(key) ?? this.mapBuilder.hasKey(key);
        };
        this.get = (key, otherwise) => {
            if (undefined !== this.source)
                return this.source.get(key, otherwise);
            return this.mapBuilder.get(key, otherwise);
        };
        this.set = (key, value) => {
            return this.addEntry([key, value]);
        };
        this.addEntry = (entry) => {
            this.checkLock();
            const preSize = this.mapBuilder.size;
            if (!this.mapBuilder.addEntry(entry))
                return false;
            this.source = undefined;
            const diff = this.mapBuilder.size - preSize;
            if (diff > 0)
                this.keyOrderBuilder.append(entry[0]);
            return true;
        };
        this.addEntries = (entries) => {
            this.checkLock();
            return Stream.from(entries).filterPure({ pred: this.addEntry }).count() > 0;
        };
        this.removeKey = (key, otherwise) => {
            this.checkLock();
            if (!this.context.mapContext.isValidKey(key)) {
                return OptLazy(otherwise);
            }
            const token = Symbol();
            const removedValue = this.mapBuilder.removeKey(key, token);
            if (token === removedValue) {
                return OptLazy(otherwise);
            }
            this.source = undefined;
            this.removeFromKeyOrderInternal(key);
            return removedValue;
        };
        // prettier-ignore
        this.removeKeys = (keys) => {
            this.checkLock();
            const notFound = Symbol();
            return (Stream.from(keys)
                .mapPure(this.removeKey, notFound)
                .countElement(notFound, { negate: true }) > 0);
        };
        // prettier-ignore
        this.updateAt = (key, update, otherwise) => {
            let oldValue;
            let found = false;
            this.modifyAt(key, {
                ifExists: (value) => {
                    oldValue = value;
                    found = true;
                    return Update(value, update);
                },
            });
            if (!found)
                return OptLazy(otherwise);
            this.source = undefined;
            return oldValue;
        };
        this.modifyAt = (key, options) => {
            this.checkLock();
            const preSize = this.mapBuilder.size;
            const changed = this.mapBuilder.modifyAt(key, options);
            if (changed)
                this.source = undefined;
            const diff = this.mapBuilder.size - preSize;
            if (diff === 0)
                return changed;
            if (diff > 0) {
                this.keyOrderBuilder.append(key);
            }
            else {
                this.removeFromKeyOrderInternal(key);
            }
            return true;
        };
        this.forEach = (f, options = {}) => {
            const { reversed = false, state = TraverseState() } = options;
            if (state.halted)
                return;
            this._lock = true;
            if (undefined !== this.source)
                this.source.forEach(f, options);
            else {
                const { halt } = state;
                const mapBuilder = this.mapBuilder;
                this.keyOrderBuilder.forEach((key, _, outerHalt) => {
                    f([key, mapBuilder.get(key, RimbuError.throwInvalidStateError)], state.nextIndex(), halt);
                    if (state.halted)
                        outerHalt();
                }, { reversed });
            }
            this._lock = false;
        };
        // prettier-ignore
        this.buildMapValues = (f) => {
            if (undefined !== this.source)
                return this.source.mapValues(f);
            if (this.size === 0)
                return this.context.empty();
            const keyOrder = this.keyOrderBuilder.build().assumeNonEmpty();
            const sourceMap = this.mapBuilder.buildMapValues(f).assumeNonEmpty();
            return this.context.createNonEmpty(keyOrder, sourceMap);
        };
        this.build = () => {
            if (undefined !== this.source)
                return this.source;
            if (this.size === 0)
                return this.context.empty();
            const keyOrder = this.keyOrderBuilder.build().assumeNonEmpty();
            const sourceMap = this.mapBuilder.build().assumeNonEmpty();
            return this.context.createNonEmpty(keyOrder, sourceMap);
        };
    }
    checkLock() {
        if (this._lock)
            RimbuError.throwModifiedBuilderWhileLoopingOverItError();
    }
    prepareMutate() {
        if (undefined === this._keyOrderBuilder || undefined === this._mapBuilder) {
            if (undefined !== this.source) {
                this._keyOrderBuilder = this.source.keyOrder.toBuilder();
                this._mapBuilder = this.source.sourceMap.toBuilder();
            }
            else if (undefined === this._keyOrderBuilder) {
                this._keyOrderBuilder = this.context.listContext.builder();
                this._mapBuilder = this.context.mapContext.builder();
            }
        }
    }
    get keyOrderBuilder() {
        this.prepareMutate();
        return this._keyOrderBuilder;
    }
    get mapBuilder() {
        this.prepareMutate();
        return this._mapBuilder;
    }
    get size() {
        return this.source?.size ?? this.keyOrderBuilder.length;
    }
    get isEmpty() {
        return this.size === 0;
    }
    removeFromKeyOrderInternal(key) {
        let index = -1;
        this.keyOrderBuilder.forEach((k, i, halt) => {
            if (Object.is(k, key)) {
                index = i;
                halt();
            }
        });
        this.keyOrderBuilder.remove(index);
    }
}
//# sourceMappingURL=builder.mjs.map